import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDiscardGuard } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, X, Paperclip, Send, File, Download, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/auth.store';
import { useFeatureFlags } from '@/common/hooks/useFeatureFlags';
import { usePermissions } from '@/common/hooks/usePermissions';
import { usersService, User } from '@/features/users/services/users.service';
import { clientsService, Client } from '@/features/clients/services/clients.service';
import { getClientDisplayName } from '@/features/clients/utils/display';
import {
  DailyTask,
  DailyTaskComment,
  DailyTaskFile,
  dailyTasksService,
  CreateCommentDto,
  DailyTaskChangeLogEntry,
  TeamCalendarTeam,
  TaskDependency,
  TaskDependencyAnchor,
} from '@/features/daily-tasks/services/daily-tasks.service';
import { taskTagsService } from '@/features/daily-tasks/services/task-tags.service';
import type { TaskTag } from '@/features/daily-tasks/services/task-tags.service';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/ui/user-avatar';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import '@/components/ui/rich-text-editor.css';
import { MentionTextarea } from '@/features/daily-tasks/components/MentionTextarea';
import { CommentCard } from '@/features/daily-tasks/components/CommentCard';
import {
  techSystemsService,
  TechSystem,
} from '@/features/tech-systems/services/tech-systems.service';
import { canAccessTechSystemsModule } from '@/features/tech-systems/utils/projects-access';
import { parseNotionDate } from '@/features/kanban/utils/parseNotionDate';
import { getNotionDateRangeStatus } from '@/features/kanban/utils/getNotionDateRangeStatus';
import {
  DAILY_TASK_DESCRIPTION_MAX_LENGTH,
  stripHtml,
} from '@/features/daily-tasks/utils/description';

interface TaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  task: DailyTask | null;
  creationContext?: 'my-dashboard' | 'admin-board' | 'other';
  readOnly?: boolean;
  allowCommentsInReadOnly?: boolean;
  projectDependencies?: TaskDependency[];
  onDeleteProjectDependency?: (dependencyId: string) => Promise<void>;
}

const DEPENDENCY_ANCHOR_LABELS: Record<TaskDependencyAnchor, string> = {
  FS: 'Fim → Início',
  SS: 'Início → Início',
  FF: 'Fim → Fim',
  SF: 'Início → Fim',
};

const TITLE_MAX_LENGTH = 255;
const DESCRIPTION_MAX_LENGTH = DAILY_TASK_DESCRIPTION_MAX_LENGTH;
const ALL_TEAMS_OPTION = '__all_teams__';

// Helper para obter o nome de exibição do usuário
const getUserDisplayName = (user: User): string => {
  if (user.fullName) {
    const firstName = user.fullName.split(' ')[0];
    return `${firstName} - ${user.email}`;
  }
  return user.email;
};

const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTimeLocalValue = (currentValue?: string | null) => {
  if (!currentValue) return '';
  const date = new Date(currentValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const looksLikeHtml = (value?: string | null) =>
  typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);

export function TaskDetailModal({
  open,
  onClose,
  task,
  creationContext = 'my-dashboard',
  readOnly = false,
  allowCommentsInReadOnly = false,
  projectDependencies = [],
  onDeleteProjectDependency,
}: TaskDetailModalProps) {
  const { user } = useAuthStore();
  const { isEnabled } = useFeatureFlags();
  const { hasPermission } = usePermissions();
  const hasTechSystems = isEnabled('TECH_SYSTEMS');
  const canSelectTechSystem =
    hasTechSystems && canAccessTechSystemsModule(hasPermission);
  const queryClient = useQueryClient();
  const [displayTask, setDisplayTask] = useState<DailyTask | null>(null);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [allTags, setAllTags] = useState<TaskTag[]>([]);
  const [teams, setTeams] = useState<TeamCalendarTeam[]>([]);
  const [comments, setComments] = useState<DailyTaskComment[]>([]);
  const [commentsHasMore, setCommentsHasMore] = useState(false);
  const [commentsLoadingMore, setCommentsLoadingMore] = useState(false);
  const [files, setFiles] = useState<DailyTaskFile[]>([]);
  const [commentText, setCommentText] = useState('');
  const [commentNotifyUserIds, setCommentNotifyUserIds] = useState<string[]>([]);
  const viewedCommentIdsRef = useRef<Set<string>>(new Set());
  const commentsScrollRef = useRef<HTMLDivElement>(null);
  const [commentsScrollEl, setCommentsScrollEl] = useState<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [changeLogs, setChangeLogs] = useState<DailyTaskChangeLogEntry[]>([]);
  const [changeLogsHasMore, setChangeLogsHasMore] = useState(false);
  const [changeLogsLoading, setChangeLogsLoading] = useState(false);
  const [deletingDependencyId, setDeletingDependencyId] = useState<string | null>(
    null,
  );
  const [techSystems, setTechSystems] = useState<TechSystem[]>([]);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const descriptionSectionRef = useRef<HTMLDivElement>(null);
  const descriptionSyncedTaskIdRef = useRef<string | null>(null);
  const savedDescriptionRef = useRef('');

  // Sincronizar displayTask com a prop task (ao abrir ou trocar de tarefa)
  useEffect(() => {
    if (open && task) {
      setDisplayTask(task);
    } else if (!open) {
      setDisplayTask(null);
    }
  }, [open, task?.id, task]);

  const loadFullTask = async () => {
    if (!task) return;
    try {
      const full = await dailyTasksService.getTask(task.id);
      setDisplayTask(full);
    } catch (e) {
      console.error('Erro ao carregar tarefa:', e);
    }
  };

  // Carregar dados quando o modal abrir (tarefa completa com responsáveis/clientes + comentários etc)
  useEffect(() => {
    if (open && task) {
      viewedCommentIdsRef.current = new Set();
      loadFullTask();
      loadUsers();
      loadClients();
      if (task.isTeamCalendar || (task.teamIds && task.teamIds.length > 0)) {
        loadTeams();
      } else {
        setTeams([]);
      }
      if (creationContext !== 'my-dashboard') {
        loadTags();
      }
      loadComments();
      loadFiles();
      if (canSelectTechSystem) {
        void loadTechSystems();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loaders não são memoizados; recarrega só ao abrir/trocar de tarefa
  }, [open, task, creationContext, canSelectTechSystem]);

  useEffect(() => {
    if (!open) {
      descriptionSyncedTaskIdRef.current = null;
      savedDescriptionRef.current = '';
      setDescriptionDraft('');
      return;
    }

    const currentTask = displayTask ?? task;
    if (!currentTask) return;

    const serverDescription = currentTask.description ?? '';

    if (descriptionSyncedTaskIdRef.current !== currentTask.id) {
      setDescriptionDraft(serverDescription);
      savedDescriptionRef.current = serverDescription;
      descriptionSyncedTaskIdRef.current = currentTask.id;
      return;
    }

    if (
      descriptionDraft === savedDescriptionRef.current &&
      serverDescription !== savedDescriptionRef.current
    ) {
      setDescriptionDraft(serverDescription);
      savedDescriptionRef.current = serverDescription;
    }
  }, [open, task?.id, displayTask?.id, displayTask?.description, task?.description, descriptionDraft, task, displayTask]);

  const loadUsers = async () => {
    try {
      const response = await usersService.getAll();
      const usersList = Array.isArray(response.data) ? response.data : (response.data as any)?.data || [];
      const normalized = usersList.filter((u: any) => u.active).map((u: any) => ({
        ...u,
        avatarUrl: u.avatarUrl ?? (u as { avatar_url?: string | null }).avatar_url ?? null,
      }));
      setUsers(normalized);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadClients = async () => {
    try {
      const clientsList = await clientsService.listClients({ isActive: true });
      setClients(clientsList);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const loadTags = async () => {
    try {
      const list = await taskTagsService.getAll();
      setAllTags(list);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const loadTechSystems = async () => {
    try {
      const systems = await techSystemsService.list({ status: 'active' });
      setTechSystems(systems);
    } catch (error) {
      console.error('Erro ao carregar sistemas:', error);
      setTechSystems([]);
    }
  };

  const loadTeams = async () => {
    try {
      const list = await dailyTasksService.getTeamCalendarTeams();
      setTeams(list);
    } catch (error) {
      console.error('Erro ao carregar times:', error);
      setTeams([]);
    }
  };

  const COMMENTS_PAGE_SIZE = 30;

  const loadComments = async () => {
    if (!task) return;
    try {
      const res = await dailyTasksService.getComments(task.id, { limit: COMMENTS_PAGE_SIZE });
      setComments(res.comments ?? []);
      setCommentsHasMore(res.hasMore ?? false);
    } catch (error) {
      console.error('Erro ao carregar comentários:', error);
    }
  };

  const loadMoreComments = async () => {
    if (!task || !commentsHasMore || commentsLoadingMore || comments.length === 0) return;
    const oldest = comments[0];
    const beforeCreatedAt = oldest?.createdAt;
    if (!beforeCreatedAt) return;
    setCommentsLoadingMore(true);
    try {
      const res = await dailyTasksService.getComments(task.id, {
        limit: COMMENTS_PAGE_SIZE,
        beforeCreatedAt,
      });
      setComments((prev) => [...(res.comments ?? []), ...prev]);
      setCommentsHasMore(res.hasMore ?? false);
    } catch (error) {
      console.error('Erro ao carregar mais comentários:', error);
    } finally {
      setCommentsLoadingMore(false);
    }
  };

  const handleCommentsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop <= 100 && commentsHasMore && !commentsLoadingMore) {
      loadMoreComments();
    }
  };

  const loadFiles = async () => {
    if (!task) return;
    try {
      const filesList = await dailyTasksService.getFiles(task.id);
      setFiles(filesList);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
    }
  };

  const loadChangeLogs = async () => {
    if (!task) return;
    setChangeLogsLoading(true);
    try {
      const res = await dailyTasksService.getChangeLogs(task.id, { limit: 50 });
      setChangeLogs(res.entries ?? []);
      setChangeLogsHasMore(res.hasMore ?? false);
    } catch (error) {
      console.error('Erro ao carregar histórico de alterações:', error);
    } finally {
      setChangeLogsLoading(false);
    }
  };

  const updateTaskMutation = useMutation({
    mutationFn: async (updates: any) => {
      if (!task) return;
      return await dailyTasksService.updateTask(task.id, updates);
    },
    onSuccess: async (updated: DailyTask | undefined, variables) => {
      queryClient.invalidateQueries({ queryKey: ['daily-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['team-calendar-tasks'] });
      setIsEditing(null);
      if (updated) {
        setDisplayTask((prev) => (prev ? { ...prev, ...updated } : updated));
      }
      if (variables?.clientIds !== undefined && task) {
        try {
          const full = await dailyTasksService.getTask(task.id);
          setDisplayTask(full);
        } catch {
          // fallback: já atualizamos com updated acima
        }
      }
    },
  });

  const createCommentMutation = useMutation({
    mutationFn: async (dto: CreateCommentDto) => {
      if (!task) return;
      return await dailyTasksService.createComment(task.id, dto);
    },
    onSuccess: async () => {
      setCommentText('');
      setCommentNotifyUserIds([]);
      await loadComments();
    },
  });

  const handleCommentViewed = useCallback(
    async (commentId: string) => {
      if (!user || viewedCommentIdsRef.current.has(commentId)) return;
      viewedCommentIdsRef.current.add(commentId);

      try {
        await dailyTasksService.markCommentViewed(commentId);
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.id !== commentId) return comment;
            const alreadyViewed = (comment.views ?? []).some(
              (view) => view.userId === user.id,
            );
            if (alreadyViewed) return comment;
            return {
              ...comment,
              views: [
                ...(comment.views ?? []),
                {
                  id: `optimistic-${commentId}`,
                  userId: user.id,
                  viewedAt: new Date().toISOString(),
                  user: {
                    id: user.id,
                    email: user.email,
                    fullName: null,
                  },
                },
              ],
            };
          }),
        );
      } catch {
        viewedCommentIdsRef.current.delete(commentId);
      }
    },
    [user],
  );

  const handleDoubleClick = (field: string, currentValue: any) => {
    if (readOnly) return;
    setIsEditing(field);
    if (field === 'clientIds') {
      setEditingValue(JSON.stringify(currentValue || []));
    } else if (
      field === 'dueDate' ||
      field === 'endDate' ||
      field === 'plannedStartAt' ||
      field === 'plannedEndAt' ||
      field === 'actualStartAt' ||
      field === 'actualEndAt'
    ) {
      setEditingValue(formatDateTimeLocalValue(currentValue));
    } else if (typeof currentValue === 'object' && currentValue !== null) {
      setEditingValue(JSON.stringify(currentValue));
    } else {
      const str = String(currentValue || '');
      if (field === 'title') {
        setEditingValue(str.slice(0, TITLE_MAX_LENGTH));
      } else {
        setEditingValue(str);
      }
    }
  };

  const handleSave = async (field: string) => {
    if (!task || readOnly) return;

    let value: any = editingValue;
    const updateField = field;

    if (field === 'clientIds') {
      try {
        value = JSON.parse(editingValue);
      } catch {
        value = [];
      }
    } else if (
      field === 'dueDate' ||
      field === 'endDate' ||
      field === 'plannedStartAt' ||
      field === 'plannedEndAt' ||
      field === 'actualStartAt' ||
      field === 'actualEndAt'
    ) {
      value = editingValue ? new Date(editingValue).toISOString() : null;
    } else if (field === 'plannedExecutionMinutes') {
      value = editingValue ? parseInt(editingValue, 10) : null;
    }

    if (field === 'dueDate' || field === 'endDate') {
      const effectiveDueDate =
        field === 'dueDate'
          ? (value as string | null)
          : (t.dueDate ? new Date(t.dueDate).toISOString() : null);
      const effectiveEndDate =
        field === 'endDate'
          ? (value as string | null)
          : (t.endDate ? new Date(t.endDate).toISOString() : null);
      if (
        effectiveDueDate &&
        effectiveEndDate &&
        new Date(effectiveEndDate).getTime() < new Date(effectiveDueDate).getTime()
      ) {
        toast.error('A data final não pode ser menor que a data inicial.');
        return;
      }
    }

    if (
      field === 'plannedStartAt' ||
      field === 'plannedEndAt' ||
      field === 'actualStartAt' ||
      field === 'actualEndAt'
    ) {
      const effectivePlannedStart =
        field === 'plannedStartAt'
          ? (value as string | null)
          : (t.plannedStartAt ? new Date(t.plannedStartAt).toISOString() : null);
      const effectivePlannedEnd =
        field === 'plannedEndAt'
          ? (value as string | null)
          : (t.plannedEndAt ? new Date(t.plannedEndAt).toISOString() : null);
      const effectiveActualStart =
        field === 'actualStartAt'
          ? (value as string | null)
          : (t.actualStartAt ? new Date(t.actualStartAt).toISOString() : null);
      const effectiveActualEnd =
        field === 'actualEndAt'
          ? (value as string | null)
          : (t.actualEndAt ? new Date(t.actualEndAt).toISOString() : null);

      if (
        effectivePlannedStart &&
        effectivePlannedEnd &&
        new Date(effectivePlannedStart).getTime() >
          new Date(effectivePlannedEnd).getTime()
      ) {
        toast.error(
          'A data final planejada não pode ser anterior à data inicial planejada.',
        );
        return;
      }

      if (
        effectiveActualStart &&
        effectiveActualEnd &&
        new Date(effectiveActualStart).getTime() >
          new Date(effectiveActualEnd).getTime()
      ) {
        toast.error(
          'A data final executada não pode ser anterior à data inicial executada.',
        );
        return;
      }
    }

    const updates: any = { [updateField]: value };
    await updateTaskMutation.mutateAsync(updates);
  };

  const handleCancelEdit = () => {
    setIsEditing(null);
    setEditingValue('');
  };

  const handleAssigneeToggle = (userId: string) => {
    if (!task || readOnly) return;
    const currentIds = (displayTask ?? task)?.assignees?.map((a) => a.userId) || [];
    if (currentIds.includes(userId)) {
      const newIds = currentIds.filter((id) => id !== userId);
      updateTaskMutation.mutate({ assigneeIds: newIds });
    } else {
      updateTaskMutation.mutate({ assigneeIds: [...currentIds, userId] });
    }
  };

  const handleClientToggle = (clientId: string) => {
    if (!task || readOnly) return;
    const currentIds = (displayTask ?? task)?.clients?.map((c) => c.clientId) ?? [];
    const newIds = currentIds.includes(clientId)
      ? currentIds.filter((id) => id !== clientId)
      : [...currentIds, clientId];
    updateTaskMutation.mutate({ clientIds: [...newIds] });
  };

  const handleTagToggle = (tagId: string) => {
    if (!task || readOnly) return;
    const currentIds = (displayTask ?? task)?.tags?.map((tag) => tag.id) ?? [];
    const newIds = currentIds.includes(tagId)
      ? currentIds.filter((id) => id !== tagId)
      : [...currentIds, tagId];
    updateTaskMutation.mutate({ tagIds: newIds });
  };

  const handleTeamToggle = (teamId: string) => {
    if (!task || readOnly) return;
    const currentIds = t.teamIds ?? [];
    const newIds = currentIds.includes(teamId)
      ? currentIds.filter((id) => id !== teamId)
      : [...currentIds, teamId];
    updateTaskMutation.mutate({ teamIds: newIds });
  };

  const handleSelectTeam = (teamId: string) => {
    if (!task || readOnly) return;

    if (teamId === ALL_TEAMS_OPTION) {
      const allTeamIds = teams.map((team) => team.id);
      updateTaskMutation.mutate({ teamIds: allTeamIds });
      return;
    }

    handleTeamToggle(teamId);
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim() || !task || (readOnly && !allowCommentsInReadOnly)) return;

    await createCommentMutation.mutateAsync({
      content: commentText,
      notifyUserIds: commentNotifyUserIds,
    });
  };

  const saveDescription = useCallback(async () => {
    if (!task || readOnly) return;

    const savedDescription = (displayTask ?? task).description ?? '';
    if (descriptionDraft === savedDescription) return;

    const plainLength = stripHtml(descriptionDraft).length;
    if (plainLength > DESCRIPTION_MAX_LENGTH) {
      toast.error(`A descrição pode ter no máximo ${DESCRIPTION_MAX_LENGTH} caracteres.`);
      return;
    }

    await updateTaskMutation.mutateAsync({
      description: descriptionDraft || null,
    });
    savedDescriptionRef.current = descriptionDraft;
  }, [task, readOnly, displayTask, descriptionDraft, updateTaskMutation]);

  const handleDescriptionSectionBlur = () => {
    window.setTimeout(() => {
      const section = descriptionSectionRef.current;
      if (!section?.contains(document.activeElement)) {
        void saveDescription();
      }
    }, 0);
  };

  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!task) throw new Error('Tarefa não encontrada');
      return dailyTasksService.uploadFile(task.id, file);
    },
    onSuccess: async () => {
      await loadFiles();
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('Arquivo enviado com sucesso.');
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message ?? error?.message ?? 'Erro ao enviar arquivo.';
      toast.error('Erro ao enviar arquivo', { description: msg });
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (file && task) {
      uploadFileMutation.mutate(file);
    }
    e.target.value = '';
  };

  // Comentário digitado ou campo em edição não se perde sem aviso ao fechar (Esc, ✕, clique fora).
  const requestClose = useDiscardGuard(commentText.trim() !== '' || isEditing !== null, () => onClose());

  if (!task) return null;

  const t = displayTask ?? task;
  const canEdit = !readOnly;
  const canComment = canEdit || allowCommentsInReadOnly;
  const savedDescription = t.description ?? '';
  const isDescriptionDirty = descriptionDraft !== savedDescription;
  const selectedTeamIds = t.teamIds ?? [];
  const selectedTeams = selectedTeamIds.map((teamId) => {
    const team = teams.find((item) => item.id === teamId);
    const members = (team?.users ?? [])
      .filter((member) => member.active !== false)
      .map((member) => ({
        id: member.id,
        name: member.fullName || member.email,
      }));
    return {
      id: teamId,
      name: team?.name ?? teamId,
      members,
    };
  });

  const workflowStatusLabels: Record<string, string> = {
    aguardando: 'Aguardando',
    em_progresso: 'Em Progresso',
    bloqueada: 'Bloqueada',
    suspensa: 'Suspensa',
    concluida: 'Concluída',
  };

  const priorityLabels: Record<string, string> = {
    baixa: 'Baixa',
    media: 'Média',
    alta: 'Alta',
    urgente: 'Urgente',
  };

  const priorityColors: Record<string, string> = {
    baixa: 'bg-blue-500',
    media: 'bg-gray-500',
    alta: 'bg-orange-500',
    urgente: 'bg-red-500',
  };

  const statusColors: Record<string, string> = {
    aguardando: 'bg-gray-500',
    em_progresso: 'bg-blue-500',
    bloqueada: 'bg-yellow-500',
    suspensa: 'bg-purple-500',
    concluida: 'bg-green-500',
  };

  return (
    <>
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent
        noWrapper
        size="xl"
        aria-describedby={undefined}
        onEscapeKeyDown={(e) => {
          // Esc na edição inline do título cancela a edição, não fecha o modal
          if (isEditing === 'title') {
            e.preventDefault();
            handleCancelEdit();
          }
        }}
        className="overflow-hidden p-0 flex flex-col"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <input
                  type="checkbox"
                  checked={t.workflowStatus === 'concluida'}
                  disabled={!canEdit}
                  onChange={(e) => {
                    if (!canEdit) return;
                    updateTaskMutation.mutate({
                      workflowStatus: e.target.checked ? 'concluida' : 'aguardando',
                    });
                  }}
                  className="w-4 h-4 shrink-0"
                  aria-label="Marcar tarefa como concluída"
                />
                {isEditing === 'title' ? (
                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Input
                        value={editingValue}
                        onChange={(e) =>
                          setEditingValue(e.target.value.slice(0, TITLE_MAX_LENGTH))
                        }
                        maxLength={TITLE_MAX_LENGTH}
                        onKeyDown={(e) => e.key === 'Enter' && handleSave('title')}
                        className="text-lg font-semibold flex-1"
                        autoFocus
                        aria-label="Título da tarefa"
                      />
                      <Button size="sm" onClick={() => handleSave('title')}>
                        Salvar
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                        Cancelar
                      </Button>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {editingValue.length}/{TITLE_MAX_LENGTH}
                    </span>
                  </div>
                ) : (
                  <DialogTitle
                    className={cn(
                      'text-lg font-semibold rounded px-1 -mx-1',
                      canEdit && 'cursor-pointer hover:bg-muted/50',
                    )}
                    onDoubleClick={() => {
                      if (canEdit) handleDoubleClick('title', t.title);
                    }}
                  >
                    {t.title}
                  </DialogTitle>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {t.workflowStatus && (
                  <Badge className={cn('text-white', statusColors[t.workflowStatus])}>
                    {workflowStatusLabels[t.workflowStatus]}
                  </Badge>
                )}
                {t.priority && (
                  <Badge className={cn('text-white', priorityColors[t.priority])}>
                    {priorityLabels[t.priority]}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {creationContext !== 'my-dashboard' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowHistoryDialog(true);
                    if (changeLogs.length === 0) {
                      void loadChangeLogs();
                    }
                  }}
                >
                  Histórico
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void requestClose(false)}
                className="shrink-0"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
          {/* Coluna Esquerda - Dados da Tarefa */}
          <div className="w-full min-w-0 border-b p-6 flex flex-col gap-4 md:w-1/2 md:border-b-0 md:border-r md:overflow-y-auto">
            {/* Seção 1 – Descrição */}
            <section
              ref={descriptionSectionRef}
              className="rounded-lg border bg-card p-4 space-y-2"
              aria-labelledby="section-description"
              onBlur={canEdit ? handleDescriptionSectionBlur : undefined}
            >
              <h3 id="section-description" className="text-sm font-semibold text-muted-foreground sr-only">Descrição</h3>
              <Label htmlFor="task-description">Descrição:</Label>
              {canEdit ? (
                <div className="space-y-2">
                  <RichTextEditor
                    value={descriptionDraft}
                    onChange={setDescriptionDraft}
                    placeholder="Descreva a tarefa com formatação..."
                    className="w-full"
                    minHeight={180}
                    maxHeight={360}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      {stripHtml(descriptionDraft).length}/{DESCRIPTION_MAX_LENGTH}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => void saveDescription()}
                      disabled={!isDescriptionDirty || updateTaskMutation.isPending}
                    >
                      {updateTaskMutation.isPending ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-2 border rounded-md min-h-[60px] min-w-0 overflow-hidden cursor-default">
                  {t.description ? (
                    looksLikeHtml(t.description) ? (
                      <div
                        className="rich-text-content prose prose-sm max-w-full dark:prose-invert"
                        dangerouslySetInnerHTML={{ __html: t.description }}
                      />
                    ) : (
                      <p className="whitespace-pre-wrap break-words text-sm">{t.description}</p>
                    )
                  ) : (
                    <span className="text-muted-foreground">Nenhuma descrição</span>
                  )}
                </div>
              )}
            </section>

            {(canSelectTechSystem || t.techSystemId || t.type === 'tech_system') && (
              <section className="rounded-lg border bg-card p-4 space-y-2">
                <Label>Sistema vinculado:</Label>
                {canEdit && canSelectTechSystem ? (
                  <Select
                    value={t.techSystemId ?? '__none__'}
                    onValueChange={(value) => {
                      updateTaskMutation.mutate({
                        techSystemId: value === '__none__' ? null : value,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Nenhum sistema" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nenhum</SelectItem>
                      {techSystems.map((system) => (
                        <SelectItem key={system.id} value={system.id}>
                          {system.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="rounded-md border px-3 py-2 text-sm">
                    {t.techSystemName || t.techSystemId || 'Nenhum'}
                  </div>
                )}
                {(t.techSystemId || t.type === 'tech_system') && (
                  <Badge variant="outline">Sistema TEC</Badge>
                )}
              </section>
            )}

            {/* Seção 2 – Status e Prioridade */}
            <section className="rounded-lg border bg-card p-4" aria-labelledby="section-status-priority">
              <h3 id="section-status-priority" className="text-sm font-semibold text-muted-foreground mb-3">Status e prioridade</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="task-status">Status:</Label>
                <Select
                  disabled={!canEdit}
                  value={t.workflowStatus || 'aguardando'}
                  onValueChange={(value) => {
                    if (!canEdit) return;
                    updateTaskMutation.mutate({ workflowStatus: value as any });
                  }}
                >
                  <SelectTrigger id="task-status" aria-label="Status do fluxo de trabalho">
                    <SelectValue>
                      {workflowStatusLabels[t.workflowStatus || 'aguardando']}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(workflowStatusLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-priority">Prioridade:</Label>
                <Select
                  disabled={!canEdit}
                  value={t.priority || 'media'}
                  onValueChange={(value) => {
                    if (!canEdit) return;
                    updateTaskMutation.mutate({ priority: value as any });
                  }}
                >
                  <SelectTrigger id="task-priority" aria-label="Prioridade da tarefa">
                    <SelectValue>
                      {priorityLabels[t.priority || 'media']}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(priorityLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            </section>

            {/* Seção 3 – Data prevista de finalização */}
            <section className="rounded-lg border bg-card p-4 space-y-2" aria-labelledby="section-due-date">
              <h3 id="section-due-date" className="text-sm font-semibold text-muted-foreground mb-3">Data prevista de finalização</h3>
            <div className="space-y-2">
              <Label htmlFor="task-due-date">Data prevista de finalização:</Label>
              {isEditing === 'dueDate' ? (
                <div className="space-y-2">
                  <Input
                    id="task-due-date"
                    type="datetime-local"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    autoFocus
                    aria-label="Data prevista de finalização"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSave('dueDate')}>
                      Salvar
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onDoubleClick={() => {
                    if (canEdit) handleDoubleClick('dueDate', t.dueDate);
                  }}
                  className={cn(
                    'p-2 border rounded-md',
                    canEdit ? 'cursor-pointer hover:bg-muted' : 'cursor-default',
                  )}
                >
                  {t.dueDate ? (
                    format(new Date(t.dueDate), "d 'de' MMM. 'de' yyyy HH:mm", {
                      locale: ptBR,
                    })
                  ) : (
                    <span className="text-muted-foreground">Definir data</span>
                  )}
                </div>
              )}
              {t.type === 'notion' && t.notionMetadata?.dueDate && (
                <div className="text-xs text-muted-foreground">
                  {(() => {
                    const statusInfo = getNotionDateRangeStatus(t.notionMetadata?.dueDate);
                    const startDate = parseNotionDate(t.notionMetadata?.dueDate);
                    const endDate =
                      typeof t.notionMetadata?.dueDate === 'object'
                        ? parseNotionDate(t.notionMetadata?.dueDate?.end)
                        : null;
                    const rangeLabel =
                      startDate && endDate
                        ? `${format(startDate, 'dd/MM/yyyy', { locale: ptBR })} - ${format(endDate, 'dd/MM/yyyy', { locale: ptBR })}`
                        : startDate
                          ? format(startDate, 'dd/MM/yyyy', { locale: ptBR })
                          : null;

                    if (!rangeLabel) return null;

                    return (
                      <span
                        className={cn(
                          statusInfo?.tone === 'danger' && 'text-red-600 dark:text-red-400',
                          statusInfo?.tone === 'warning' &&
                            'text-yellow-600 dark:text-yellow-400',
                        )}
                      >
                        Intervalo Notion: {rangeLabel}
                        {statusInfo?.label ? ` (${statusInfo.label})` : ''}
                      </span>
                    );
                  })()}
                </div>
              )}
            </div>
            </section>

            {t.techSystemId && (
              <section className="rounded-lg border bg-card p-4 space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  Datas do projeto
                </h3>
                {([
                  ['plannedStartAt', 'Início planejado', t.plannedStartAt],
                  ['plannedEndAt', 'Fim planejado', t.plannedEndAt],
                  ['actualStartAt', 'Início executado', t.actualStartAt],
                  ['actualEndAt', 'Fim executado', t.actualEndAt],
                ] as const).map(([field, label, value]) => (
                  <div key={field} className="space-y-2">
                    <Label>{label}:</Label>
                    {isEditing === field ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="datetime-local"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          min={
                            field === 'plannedEndAt'
                              ? formatDateTimeLocalValue(t.plannedStartAt)
                              : field === 'actualEndAt'
                                ? formatDateTimeLocalValue(t.actualStartAt)
                                : undefined
                          }
                          max={
                            field === 'plannedStartAt'
                              ? formatDateTimeLocalValue(t.plannedEndAt)
                              : field === 'actualStartAt'
                                ? formatDateTimeLocalValue(t.actualEndAt)
                                : undefined
                          }
                          autoFocus
                        />
                        <Button size="sm" onClick={() => handleSave(field)}>
                          Salvar
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <div
                        onDoubleClick={() => {
                          if (canEdit) handleDoubleClick(field, value);
                        }}
                        className={cn(
                          'p-2 border rounded-md text-sm',
                          canEdit ? 'cursor-pointer hover:bg-muted' : 'cursor-default',
                        )}
                      >
                        {value
                          ? format(new Date(String(value)), "d 'de' MMM. 'de' yyyy HH:mm", {
                              locale: ptBR,
                            })
                          : 'Não definido'}
                      </div>
                    )}
                  </div>
                ))}

                {projectDependencies.length > 0 && (
                  <div className="space-y-3 border-t pt-4">
                    <h4 className="text-sm font-medium">Dependências</h4>
                    {projectDependencies.some(
                      (dependency) => dependency.successorTaskId === t.id,
                    ) && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">Depende de</p>
                        <ul className="space-y-1">
                          {projectDependencies
                            .filter(
                              (dependency) => dependency.successorTaskId === t.id,
                            )
                            .map((dependency) => (
                              <li
                                key={dependency.id}
                                className="flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-sm"
                              >
                                <span className="min-w-0 truncate">
                                  {dependency.predecessorTitle ?? 'Tarefa'} (
                                  {DEPENDENCY_ANCHOR_LABELS[dependency.anchorType]})
                                </span>
                                {canEdit && onDeleteProjectDependency && (
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    aria-label="Remover dependência"
                                    className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                                    disabled={deletingDependencyId === dependency.id}
                                    onClick={() => {
                                      void (async () => {
                                        setDeletingDependencyId(dependency.id);
                                        try {
                                          await onDeleteProjectDependency(
                                            dependency.id,
                                          );
                                          toast.success('Dependência removida');
                                        } catch {
                                          toast.error(
                                            'Não foi possível remover a dependência',
                                          );
                                        } finally {
                                          setDeletingDependencyId(null);
                                        }
                                      })();
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                    {projectDependencies.some(
                      (dependency) => dependency.predecessorTaskId === t.id,
                    ) && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">Bloqueia</p>
                        <ul className="space-y-1">
                          {projectDependencies
                            .filter(
                              (dependency) =>
                                dependency.predecessorTaskId === t.id,
                            )
                            .map((dependency) => (
                              <li
                                key={dependency.id}
                                className="flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-sm"
                              >
                                <span className="min-w-0 truncate">
                                  {dependency.successorTitle ?? 'Tarefa'} (
                                  {DEPENDENCY_ANCHOR_LABELS[dependency.anchorType]})
                                </span>
                                {canEdit && onDeleteProjectDependency && (
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    aria-label="Remover dependência"
                                    className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                                    disabled={deletingDependencyId === dependency.id}
                                    onClick={() => {
                                      void (async () => {
                                        setDeletingDependencyId(dependency.id);
                                        try {
                                          await onDeleteProjectDependency(
                                            dependency.id,
                                          );
                                          toast.success('Dependência removida');
                                        } catch {
                                          toast.error(
                                            'Não foi possível remover a dependência',
                                          );
                                        } finally {
                                          setDeletingDependencyId(null);
                                        }
                                      })();
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {t.isTeamCalendar && (
              <section className="rounded-lg border bg-card p-4 space-y-2" aria-labelledby="section-end-date">
                <h3 id="section-end-date" className="text-sm font-semibold text-muted-foreground mb-3">
                  Data final (opcional)
                </h3>
                <Label htmlFor="task-end-date">Data final:</Label>
                {isEditing === 'endDate' ? (
                  <div className="space-y-2">
                    <Input
                      id="task-end-date"
                      type="datetime-local"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      autoFocus
                      aria-label="Data final da tarefa"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSave('endDate')}>
                        Salvar
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                        Cancelar
                      </Button>
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setEditingValue('');
                            void handleSave('endDate');
                          }}
                        >
                          Remover data final
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    onDoubleClick={() => {
                      if (canEdit) handleDoubleClick('endDate', t.endDate);
                    }}
                    className={cn(
                      'p-2 border rounded-md',
                      canEdit ? 'cursor-pointer hover:bg-muted' : 'cursor-default',
                    )}
                  >
                    {t.endDate ? (
                      format(new Date(t.endDate), "d 'de' MMM. 'de' yyyy HH:mm", {
                        locale: ptBR,
                      })
                    ) : (
                      <span className="text-muted-foreground">Sem data final</span>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* Seção 4 – Responsáveis */}
            <section className="rounded-lg border bg-card p-4 space-y-2" aria-labelledby="section-assignees">
              <h3 id="section-assignees" className="text-sm font-semibold text-muted-foreground mb-3">Responsáveis</h3>
              <Label htmlFor="task-assignees">Responsáveis:</Label>
              <Select
                disabled={!canEdit}
                value=""
                onValueChange={(userId) => {
                  if (!canEdit) return;
                  if (userId) handleAssigneeToggle(userId);
                }}
              >
                <SelectTrigger id="task-assignees" aria-label="Adicionar responsável">
                  <SelectValue placeholder="Selecione um responsável..." />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter((u) => !t.assignees?.some((a) => a.userId === u.id))
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {getUserDisplayName(u)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {(t.assignees ?? []).length > 0 ? (
                <div className="space-y-2 mt-2">
                  {(t.assignees ?? []).map((assignee) => {
                    const assigneeUser = users.find((u) => u.id === assignee.userId);
                    if (!assigneeUser) return null;
                    const isLocked =
                      creationContext === 'my-dashboard' && user && assignee.userId === user.id;
                    return (
                      <div
                        key={assignee.id}
                        className="flex items-center justify-between p-2 bg-muted rounded-md border"
                      >
                        <div className="flex items-center gap-2">
                          <UserAvatar user={assigneeUser} size="md" />
                          <span
                            className={`${isLocked ? 'font-semibold' : ''} text-foreground`}
                          >
                            {getUserDisplayName(assigneeUser)}
                            {isLocked && ' (Você)'}
                          </span>
                        </div>
                        {canEdit && !isLocked && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAssigneeToggle(assignee.userId)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                            aria-label={`Remover ${getUserDisplayName(assigneeUser)}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum responsável</p>
              )}
            </section>

            {(task.isTeamCalendar || selectedTeams.length > 0) && (
              <section className="rounded-lg border bg-card p-4 space-y-2" aria-labelledby="section-teams">
                <h3 id="section-teams" className="text-sm font-semibold text-muted-foreground mb-3">
                  Times selecionados
                </h3>
                {canEdit && (
                  <div className="space-y-2">
                    <Label htmlFor="task-teams">Adicionar time</Label>
                    <Select
                      value=""
                      onValueChange={(teamId) => {
                        if (!canEdit) return;
                        if (teamId) handleSelectTeam(teamId);
                      }}
                    >
                      <SelectTrigger id="task-teams" aria-label="Adicionar time">
                        <SelectValue placeholder="Selecione um time..." />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.length > 0 && (t.teamIds ?? []).length < teams.length && (
                          <SelectItem value={ALL_TEAMS_OPTION}>Todos</SelectItem>
                        )}
                        {teams
                          .filter((team) => !(t.teamIds ?? []).includes(team.id))
                          .map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                              {team.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {selectedTeams.length > 0 ? (
                  <div className="space-y-2 mt-2">
                    {selectedTeams.map((team) => (
                      <div key={team.id} className="rounded-md border p-2 bg-muted/30">
                        <div className="flex items-center justify-between gap-2">
                          <Badge variant="outline">{team.name}</Badge>
                          {canEdit && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTeamToggle(team.id)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              aria-label={`Remover time ${team.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {team.members.length > 0 ? (
                            team.members.map((member) => (
                              <Badge key={member.id} variant="secondary" className="text-[10px]">
                                {member.name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Sem membros carregados para este time.
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum time selecionado.</p>
                )}
              </section>
            )}

            {/* Seção 5 – Cliente (apenas admin-board) */}
            {creationContext !== 'my-dashboard' && (
              <section className="rounded-lg border bg-card p-4 space-y-2" aria-labelledby="section-clients">
                <h3 id="section-clients" className="text-sm font-semibold text-muted-foreground mb-3">Cliente</h3>
                <Label htmlFor="task-clients">Cliente:</Label>
                <Select
                  disabled={!canEdit}
                  value=""
                  onValueChange={(clientId) => {
                    if (!canEdit) return;
                    if (clientId) handleClientToggle(clientId);
                  }}
                >
                  <SelectTrigger id="task-clients" aria-label="Adicionar cliente">
                    <SelectValue placeholder="Selecione um cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients
                      .filter((c) => !(t.clients ?? []).some((tc) => tc.clientId === c.id))
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {getClientDisplayName(c)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {(t.clients ?? []).length > 0 ? (
                  <div className="space-y-2 mt-2">
                    {(t.clients ?? []).map((taskClient) => {
                      const client = clients.find((c) => c.id === taskClient.clientId);
                      if (!client) return null;
                      return (
                        <div
                          key={taskClient.id}
                          className="flex items-center justify-between p-2 bg-muted rounded-md border"
                        >
                          <span className="text-foreground font-medium">{getClientDisplayName(client)}</span>
                          {canEdit && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleClientToggle(taskClient.clientId)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              aria-label={`Remover ${getClientDisplayName(client)}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum cliente</p>
                )}
              </section>
            )}

            {/* Seção Tags */}
            {creationContext !== 'my-dashboard' && (
              <section className="rounded-lg border bg-card p-4 space-y-2" aria-labelledby="section-tags">
                <h3 id="section-tags" className="text-sm font-semibold text-muted-foreground mb-3">Tags</h3>
                <Label htmlFor="task-tags">Tags:</Label>
                <Select
                  disabled={!canEdit}
                  value=""
                  onValueChange={(tagId) => {
                    if (!canEdit) return;
                    if (tagId) handleTagToggle(tagId);
                  }}
                >
                  <SelectTrigger id="task-tags" aria-label="Adicionar tag">
                    <SelectValue placeholder="Selecione uma tag..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allTags
                      .filter((tag) => !(t.tags ?? []).some((tt) => tt.id === tag.id))
                      .map((tag) => (
                        <SelectItem key={tag.id} value={tag.id}>
                          <span className="flex items-center gap-2">
                            <span
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {(t.tags ?? []).length > 0 ? (
                  <div className="space-y-2 mt-2">
                    {(t.tags ?? []).map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center justify-between p-2 bg-muted rounded-md border"
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          <span className="text-sm font-medium">{tag.name}</span>
                        </span>
                        {canEdit && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTagToggle(tag.id)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                            aria-label={`Remover tag ${tag.name}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhuma tag</p>
                )}
              </section>
            )}
          </div>

          {/* Coluna Direita - Comentários */}
          <div className="w-full shrink-0 flex flex-col md:w-1/2 md:shrink md:min-h-0">
            <div
              ref={(element) => {
                commentsScrollRef.current = element;
                setCommentsScrollEl(element);
              }}
              onScroll={handleCommentsScroll}
              className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0"
            >
              <h3 className="font-semibold text-lg">Comentários</h3>

              {/* Arquivos anexados - exibidos como mensagens/cards */}
              {files.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Arquivos anexados</h4>
                  <div className="space-y-2">
                    {files.map((file) => {
                      const ext = getFileExtension(file.fileName);
                      return (
                        <div
                          key={file.id}
                          className="flex items-center gap-3 rounded-lg border bg-card p-3 hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                            <File className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium" title={file.fileName}>
                              {file.fileName}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {ext && (
                                <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
                                  .{ext}
                                </span>
                              )}
                              <span>{formatFileSize(file.fileSize)}</span>
                              {file.uploadedAt && (
                                <span>
                                  {format(new Date(file.uploadedAt), 'dd/MM HH:mm', { locale: ptBR })}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex shrink-0 gap-1">
                            {file.url && (
                              <>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 gap-1"
                                  onClick={() => window.open(file.url, '_blank')}
                                  title="Abrir"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  Abrir
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 gap-1"
                                  onClick={() => {
                                    const a = document.createElement('a');
                                    a.href = file.url!;
                                    a.download = file.fileName;
                                    a.target = '_blank';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                  }}
                                  title="Baixar"
                                >
                                  <Download className="h-3.5 w-3.5" />
                                  Baixar
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {commentsLoadingMore && (
                <div className="text-sm text-muted-foreground py-2">Carregando mais...</div>
              )}
              <div className="space-y-4">
                {(comments ?? []).map((comment) => (
                  <CommentCard
                    key={comment.id}
                    comment={comment}
                    currentUserId={user?.id}
                    scrollRootRef={commentsScrollEl}
                    onViewed={handleCommentViewed}
                  />
                ))}
              </div>
            </div>
            <div className="shrink-0 border-t p-4 bg-muted/30 space-y-2">
              {canComment ? (
                <>
                  <MentionTextarea
                    value={commentText}
                    onChange={(text, notifyUserIds) => {
                      setCommentText(text);
                      setCommentNotifyUserIds(notifyUserIds);
                    }}
                    users={users}
                    disabled={createCommentMutation.isPending}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <>
                          <Input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={handleFileSelect}
                            disabled={uploadFileMutation.isPending}
                            aria-label="Anexar arquivo"
                            id="task-file-upload"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadFileMutation.isPending}
                            aria-label="Anexar arquivo"
                          >
                            <Paperclip className="h-4 w-4 mr-1" />
                            {uploadFileMutation.isPending ? 'Enviando...' : 'Anexar'}
                          </Button>
                        </>
                      )}
                    </div>
                    <Button
                      onClick={handleCommentSubmit}
                      disabled={!commentText.trim() || createCommentMutation.isPending}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Enviar
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Você possui acesso somente para visualização.
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    {creationContext !== 'my-dashboard' && (
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent
          className="max-w-3xl"
        >
          <DialogHeader>
            <div className="flex items-center justify-between gap-2">
              <DialogTitle>Histórico de alterações</DialogTitle>
              <Button
                variant="outline"
                size="icon"
                onClick={() => void loadChangeLogs()}
                disabled={changeLogsLoading}
                title="Atualizar histórico"
                aria-label="Atualizar histórico"
              >
                <RefreshCw className={cn('h-4 w-4', changeLogsLoading && 'animate-spin')} />
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {changeLogsLoading && (
              <p className="text-sm text-muted-foreground">Carregando histórico...</p>
            )}
            {!changeLogsLoading && changeLogs.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Nenhuma alteração registrada para esta tarefa.
              </p>
            )}
            {changeLogs.map((entry) => {
              const changedAt = format(new Date(entry.createdAt), 'dd/MM/yyyy HH:mm', {
                locale: ptBR,
              });
              const actorName =
                entry.changedBy?.fullName || entry.changedBy?.email || 'Usuário';

              const formatValue = (value: any): string => {
                if (value == null) return 'vazio';
                if (Array.isArray(value)) {
                  if (value.length === 0) return 'vazio';
                  return value.join(', ');
                }
                if (typeof value === 'object') {
                  try {
                    return JSON.stringify(value);
                  } catch {
                    return String(value);
                  }
                }
                return String(value);
              };

              return (
                <div
                  key={entry.id}
                  className="rounded-md border bg-card p-3 text-sm space-y-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {actorName} alterou {entry.field}
                    </span>
                    <span className="text-xs text-muted-foreground">{changedAt}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <div>
                      <span className="font-semibold">De: </span>
                      <span>{formatValue(entry.oldValue)}</span>
                    </div>
                    <div>
                      <span className="font-semibold">Para: </span>
                      <span>{formatValue(entry.newValue)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {changeLogsHasMore && (
              <p className="text-xs text-muted-foreground">
                Há mais alterações antigas não carregadas.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}
